import { N_TIMELINE, NOW_IDX, STEP_MIN, idxToOffsetMin, idxToClockTime, offsetLabel } from '../../lib/time';

export const TIMELINE_DATA = Array.from({ length: N_TIMELINE + 1 }, (_, i) => i);

// xAxisCommon: shared category-axis config. `nowIdx` defaults to the legacy
// midpoint (96) so callers that don't yet pass it keep their original render.
// When the adaptive-horizon caller passes a shifted nowIdx, the "Nu" label
// floats with it and all other hour-labels stay in correct relative time.
export function xAxisCommon(now: Date, nowIdx: number = NOW_IDX) {
  return {
    type: 'category' as const,
    data: TIMELINE_DATA,
    axisLabel: {
      color: '#9ca3af', fontSize: 9, interval: 23, margin: 6,
      formatter: (_v: unknown, idx: number) => {
        if (idx % 24 !== 0) return '';
        const time = idxToClockTime(idx, now, nowIdx);
        const offMin = idxToOffsetMin(idx, nowIdx);
        if (offMin === 0) return `{tijd|${time}}\n{nu|Nu}`;
        return `{tijd|${time}}\n{offset|${offsetLabel(offMin)}}`;
      },
      rich: {
        tijd:   { color: '#374151', fontSize: 10, fontWeight: 600, padding: [0, 0, 2, 0] },
        offset: { color: '#9ca3af', fontSize:  9 },
        nu:     { color: '#dc2626', fontSize: 10, fontWeight: 700 },
      },
    },
    axisLine: { lineStyle: { color: '#e5e7eb' } },
    splitLine: { show: false },
  };
}

// nowLineAt: build a "Nu" markLine pinned to the supplied nowIdx. The
// constant export `nowLine` below keeps backward compatibility for tests +
// callers still wired to the static midpoint.
export function nowLineAt(nowIdx: number) {
  return {
    silent: true,
    symbol: 'none' as const,
    data: [{
      xAxis: nowIdx,
      lineStyle: { color: '#dc2626', width: 1.5 },
      label: { show: true, position: 'insideEndTop', formatter: 'NU',
               color: '#dc2626', fontSize: 10, fontWeight: 700 },
    }],
  };
}
export const nowLine = nowLineAt(NOW_IDX);

// forecastShadeAt: build the future-area shade from nowIdx to the right edge.
// Same backward-compat pattern as nowLineAt.
export function forecastShadeAt(nowIdx: number) {
  return [[
    { xAxis: nowIdx, itemStyle: { color: 'rgba(15, 23, 42, 0.025)' } },
    { xAxis: N_TIMELINE },
  ]];
}
export const forecastShade = forecastShadeAt(NOW_IDX);

// EV-band kleuren (gedeeld door alle charts zodat dezelfde source overal
// dezelfde tint krijgt). Hint = anticipatie (lichter, geen EV gezien);
// detector + fusion = realtime grond-truth (donkerder, EV laadt NU). Voor
// de gebruiker is dat onderscheid belangrijker dan "uit welke bron komt het
// volume" — fusion krijgt daarom dezelfde donker-cyaan als pure detector.
export const EV_HINT_COLOR = '#0ea5e9';
export const EV_DETECTOR_COLOR = '#0369a1';

type EvBandSource = 'hint' | 'detector' | 'fusion';

function evBandColor(source: EvBandSource): string {
  // 'fusion' en 'detector' delen kleur: in beide gevallen weet de gebruiker
  // "EV is er nu". Het verschil (detector-only window vs hint-uitgebreid
  // window) wordt zichtbaar via de band-LENGTE, niet via kleur.
  return source === 'hint' ? EV_HINT_COLOR : EV_DETECTOR_COLOR;
}

// EV-laad band data voor markArea — gedeeld tussen Price/Trend/Combined zodat
// de visuele uitdrukking overal hetzelfde is.
export function evBandMarkArea(
  bands: { startIdx: number; endIdx: number; source: EvBandSource }[],
  fillAlpha: number = 0.18,
) {
  return bands.map(b => {
    const color = evBandColor(b.source);
    return [
      {
        xAxis: b.startIdx,
        itemStyle: {
          color: rgba(color, fillAlpha),
          borderColor: color,
          borderWidth: 1,
          borderType: 'dashed',
        },
      },
      { xAxis: b.endIdx },
    ];
  });
}

// Verticale lijn op het verwachte EV-aankomst-tijdstip. Gebruikt dezelfde
// hint-kleur als de band zodat ze visueel bij elkaar horen. Label boven de
// lijn toont uur + leeg-percentage zodat een gebruiker meteen ziet *waarom*
// de discharge-window er voor is uitgehaald.
export function evArrivalLine(idx: number, label: string) {
  return {
    silent: true,
    symbol: 'none' as const,
    data: [{
      xAxis: idx,
      lineStyle: { color: EV_HINT_COLOR, width: 1.5, type: 'dashed' as const },
      label: {
        show: true,
        position: 'insideEndTop' as const,
        formatter: label,
        color: EV_HINT_COLOR,
        fontSize: 10,
        fontWeight: 600,
      },
    }],
  };
}

// SOC-piek-dot bij EV-aankomst. Klein cirkeltje op de SOC·f lijn dat aangeeft
// "dit is het % dat we voor je gereserveerd hebben". Tooltip-label toont de
// exacte waarde zodat een gebruiker het ook in cijfers ziet.
export function evArrivalSocDot(idx: number, socPct: number) {
  return {
    silent: false,
    symbol: 'circle' as const,
    symbolSize: 9,
    data: [{
      xAxis: idx,
      yAxis: socPct,
      itemStyle: {
        color: EV_HINT_COLOR,
        borderColor: '#fff',
        borderWidth: 2,
      },
      label: {
        show: true,
        position: 'top' as const,
        formatter: `${Math.round(socPct)}%`,
        color: EV_HINT_COLOR,
        fontSize: 10,
        fontWeight: 700,
      },
    }],
  };
}

// Handmatige ingreep (force-actie of strategie 'manual' via het dashboard):
// diagonale arcering over de strategieband heen. Bram, 2026-10-17: "Als het
// een handmatige actie was moeten we dat veld van stippellijntjes of
// diagonale lijnen o.i.d. voorzien in de grafieken." Aanleiding was een
// force-charge op NS die in de grafiek niet te onderscheiden was van een
// planner-besluit. De bron is history.mode per 5-min bucket, dus oude
// ingrepen tonen met terugwerkende kracht mee.
export const MANUAL_HATCH_COLOR = '#475569';

let manualHatchPattern: { image: HTMLCanvasElement; repeat: 'repeat' } | null | undefined;

// Canvas-patroon met diagonale lijnen. Valt terug op null waar geen 2D-context
// bestaat (happy-dom in de tests); de aanroeper kiest dan een vlakke tint.
export function manualHatchFill(): { image: HTMLCanvasElement; repeat: 'repeat' } | null {
  if (manualHatchPattern !== undefined) return manualHatchPattern;
  manualHatchPattern = null;
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = 8; c.height = 8;
  try {
    const ctx = c.getContext?.('2d');
    if (!ctx) return null;
    ctx.strokeStyle = rgba(MANUAL_HATCH_COLOR, 0.55);
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    // Drie segmenten zodat de lijnen naadloos over de tegelrand doorlopen.
    ctx.moveTo(0, 8); ctx.lineTo(8, 0);
    ctx.moveTo(-2, 2); ctx.lineTo(2, -2);
    ctx.moveTo(6, 10); ctx.lineTo(10, 6);
    ctx.stroke();
  } catch {
    // Onvolledige canvas-implementatie (happy-dom): vlakke tint als terugval.
    return null;
  }
  manualHatchPattern = { image: c, repeat: 'repeat' };
  return manualHatchPattern;
}

export function manualBandMarkArea(bands: { startIdx: number; endIdx: number }[]) {
  if (bands.length === 0) return [];
  const fill = manualHatchFill();
  return bands.map(b => [
    {
      xAxis: b.startIdx,
      itemStyle: {
        color: fill ?? rgba(MANUAL_HATCH_COLOR, 0.15),
        borderColor: MANUAL_HATCH_COLOR,
        borderWidth: 1,
        borderType: 'dotted',
      },
      // Label alleen waar het past (vanaf zes kwartierbuckets); een smallere
      // band leest via de arcering en de legenda.
      label: {
        show: b.endIdx - b.startIdx >= 6, position: 'insideTop' as const, formatter: 'Handmatig',
        color: MANUAL_HATCH_COLOR, fontSize: 9, fontWeight: 600,
      },
    },
    { xAxis: b.endIdx },
  ]);
}

export function rgba(hex: string, a: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

// Reserve-lock visualisatie — dashed cyaan horizontale lijn op SOC-niveau
// over [now, expires_at]. Per design `control-reserve-design.md` Pattern 3.
// Apart cyaan-tint van Grid (#22d3ee) om visuele verwarring te vermijden.
export const RESERVE_LOCK_COLOR = '#06b6d4';

/**
 * Bouw een echarts markLine-config voor de reserve-lock. Returnt null als de
 * lock niet renderbaar is (verlopen, ongeldige datum, of korter dan één 15-min
 * timeline-bucket).
 *
 * Resultaat plug je op de SOC-series van de chart:
 *
 *   const lockML = reserveLockMarkLine(lock, now);
 *   ...series: [{ name: 'SOC', ..., ...(lockML ? { markLine: lockML } : {}) }]
 *
 * De lijn loopt vanaf `nowIdx` (lock is altijd nu actief) tot `endIdx`
 * (clamped op `N_TIMELINE` voor lange locks > timeline-horizon). `nowIdx`
 * is optioneel en defaultt naar de legacy midpoint NOW_IDX zodat bestaande
 * tests + niet-adaptive callers ongewijzigd blijven.
 */
export function reserveLockMarkLine(
  lock: { soc_pct: number; expires_at_utc: string },
  now: Date,
  nowIdx: number = NOW_IDX,
): {
  silent: true;
  symbol: 'none';
  data: [unknown, unknown][];
} | null {
  const expires = Date.parse(lock.expires_at_utc);
  if (!Number.isFinite(expires)) return null;

  const deltaMin = (expires - now.getTime()) / 60_000;
  if (deltaMin < STEP_MIN) return null; // korter dan 1 bucket → niet zinvol

  const endIdx = Math.min(N_TIMELINE, Math.round(nowIdx + deltaMin / STEP_MIN));
  if (endIdx <= nowIdx) return null;

  const expiresLocal = new Date(expires);
  const hh = String(expiresLocal.getHours()).padStart(2, '0');
  const mm = String(expiresLocal.getMinutes()).padStart(2, '0');
  const labelText = `Lock ${lock.soc_pct}% tot ${hh}:${mm}`;

  return {
    silent: true,
    symbol: 'none' as const,
    data: [[
      {
        xAxis: nowIdx,
        yAxis: lock.soc_pct,
        lineStyle: { color: RESERVE_LOCK_COLOR, width: 1.5, type: 'dashed' as const },
      },
      {
        xAxis: endIdx,
        yAxis: lock.soc_pct,
        label: {
          show: true,
          position: 'end' as const,
          formatter: labelText,
          color: RESERVE_LOCK_COLOR,
          fontSize: 10,
          fontWeight: 600,
        },
      },
    ]],
  };
}
